// ---------------------------------------------------------------------------
// regionRoadIndex — generalized, region-gated "Pleasant Road Quality" levers.
//
// Morgan is building hand-curated road-quality lists "for basically everywhere"
// (Hudson Valley was the first; this module holds the rest). Each region is a
// bounded box + a list of named roads scored 0–3:
//   3 = actively prefer   2 = prefer when reasonable
//   1 = neutral connector  0 = penalize heavily / avoid
//
// Same idea and same math as hudsonRoadIndex.ts, but keyed by region so one
// engine covers Paris, the Riviera, Provence, Vermont, and anything added next.
// Hudson stays in its own module on purpose — its bbox is disjoint from every
// region here, so a route matches at most one lever and the two never double-
// count. Folding Hudson in later is a drop-in (add its data + bbox below).
//
// Matching is by road NAME (and numbered designation) against the route's
// turn-by-turn steps — no geocoding needed for the score lever. Geometry-precise
// scenic injection comes later via the resolver, per region.
// ---------------------------------------------------------------------------

import parisRoads from "@/content/roadIndex/paris.json";
import rivieraRoads from "@/content/roadIndex/riviera.json";
import provenceRoads from "@/content/roadIndex/provence.json";
import vermontRoads from "@/content/roadIndex/vermont.json";
import franceRoads from "@/content/roadIndex/france.json";
import type { LngLat } from "@/utils/polyline";

export interface RegionRoad {
  town: string;
  road: string;
  score: number;
  type: string | null;
  behavior: string | null;
  why: string | null;
  anchors: string | null;
}

export interface RegionBBox {
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

export interface RegionDef {
  id: string;
  label: string;
  bbox: RegionBBox;
  roads: RegionRoad[];
  /** Lazily-built name/number → score map. */
  scoreByKey: Map<string, number>;
}

/** Strip accents and non-alphanumerics so "Route de Sénanque" and the Mapbox
 *  step "Route de Senanque" collapse to the same key. Returns null for keys too
 *  short to match safely. */
function namedKey(name: string): string | null {
  const key = name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  return key.length >= 5 ? key : null;
}

/** Numbered designation → canonical key that keeps the road CLASS distinct
 *  (French D7 ≠ N7 ≠ M7; Interstate ≠ US route). US state routes (VT-100,
 *  NY-22) collapse to a generic "rt100" since the region already implies the
 *  state and Mapbox labels them inconsistently ("Route 100" vs "VT-100"). */
export function numberedKey(name: string): string | null {
  const s = name.toUpperCase();
  let m = s.match(/\bI[-\s]?(\d+[A-Z]?)\b/); // Interstate
  if (m) return `i${m[1].toLowerCase()}`;
  m = s.match(/\bUS[-\s]?(\d+[A-Z]?)\b/); // US highway
  if (m) return `us${m[1].toLowerCase()}`;
  m = s.match(/\b(?:VT|NH|NY|MA|ME|CT|RI)[-\s]?(\d+[A-Z]?)\b/); // New-England/NY state route
  if (m) return `rt${m[1].toLowerCase()}`;
  m = s.match(/\b(?:ROUTE|RTE|RT|SR|STATE\s+ROUTE)[-\s]?(\d+[A-Z]?)\b/); // generic route
  if (m) return `rt${m[1].toLowerCase()}`;
  m = s.match(/\bA[-\s]?(\d{1,3})\b/); // French autoroute (A8, A86)
  if (m) return `a${m[1]}`;
  m = s.match(/\bN[-\s]?(\d{1,4})\b/); // French national (N118)
  if (m) return `n${m[1]}`;
  m = s.match(/\bR?D[-\s]?(\d{1,4})\b/); // French departmental (D177, RD6098)
  if (m) return `d${m[1]}`;
  m = s.match(/\bM[-\s]?(\d{2,4})\b/); // Métropole road (M6098)
  if (m) return `m${m[1]}`;
  return null;
}

function buildScoreMap(roads: RegionRoad[]): Map<string, number> {
  const map = new Map<string, number>();
  // Conflicting scores across towns resolve to the one furthest from neutral
  // (1) — the stronger signal wins, matching the Hudson lever.
  const consider = (key: string | null, score: number) => {
    if (!key) return;
    const prev = map.get(key);
    if (prev === undefined || Math.abs(score - 1) > Math.abs(prev - 1)) map.set(key, score);
  };
  for (const r of roads) {
    if (r.score == null || !r.road) continue;
    // "Basse Corniche / M6098" registers BOTH the name and the number.
    for (const part of r.road.split("/").map((p) => p.trim())) {
      consider(numberedKey(part), r.score);
      if (!numberedKey(part)) consider(namedKey(part), r.score);
    }
  }
  return map;
}

// Region boxes are generous on purpose (a coarse gate); they're disjoint from
// each other and from the Hudson Valley box, so a route is scored by at most
// one region's list.
export const REGIONS: RegionDef[] = [
  {
    id: "paris",
    label: "western Paris",
    bbox: { minLng: 1.7, maxLng: 2.45, minLat: 48.55, maxLat: 49.0 },
    roads: parisRoads as RegionRoad[],
    scoreByKey: buildScoreMap(parisRoads as RegionRoad[]),
  },
  {
    id: "riviera",
    label: "the Riviera",
    bbox: { minLng: 6.55, maxLng: 7.55, minLat: 43.4, maxLat: 43.95 },
    roads: rivieraRoads as RegionRoad[],
    scoreByKey: buildScoreMap(rivieraRoads as RegionRoad[]),
  },
  {
    id: "provence",
    label: "the Luberon",
    bbox: { minLng: 4.7, maxLng: 5.7, minLat: 43.5, maxLat: 44.15 },
    roads: provenceRoads as RegionRoad[],
    scoreByKey: buildScoreMap(provenceRoads as RegionRoad[]),
  },
  {
    id: "vermont",
    label: "Vermont",
    bbox: { minLng: -73.45, maxLng: -71.45, minLat: 42.72, maxLat: 45.02 },
    roads: vermontRoads as RegionRoad[],
    scoreByKey: buildScoreMap(vermontRoads as RegionRoad[]),
  },
  // France-wide fallback for the rest of the country (Loire, Bordeaux, Alsace,
  // the Alps, Normandy, Brittany, Corsica, …). MUST stay LAST: its box overlaps
  // the detailed Paris / Riviera / Luberon boxes above, and regionForCoords
  // returns the FIRST match — so those keep precedence in their own areas and
  // this only catches routes elsewhere in France.
  {
    id: "france",
    label: "France",
    bbox: { minLng: -5.2, maxLng: 9.7, minLat: 41.2, maxLat: 51.2 },
    roads: franceRoads as RegionRoad[],
    scoreByKey: buildScoreMap(franceRoads as RegionRoad[]),
  },
];

function inBox(p: LngLat, b: RegionBBox): boolean {
  return p[0] >= b.minLng && p[0] <= b.maxLng && p[1] >= b.minLat && p[1] <= b.maxLat;
}

/** The curated region a route runs through (by its midpoint), or null. */
export function regionForCoords(coords: LngLat[] | undefined): RegionDef | null {
  if (!coords || coords.length === 0) return null;
  const mid = coords[Math.floor(coords.length / 2)];
  return REGIONS.find((r) => inBox(mid, r.bbox)) ?? null;
}

/** The score (0–3) for a step's road name within a region, or null if unlisted. */
export function regionScoreForRoad(
  region: RegionDef,
  roadName: string | null | undefined,
): number | null {
  if (!roadName) return null;
  const num = numberedKey(roadName);
  if (num && region.scoreByKey.has(num)) return region.scoreByKey.get(num)!;
  const named = namedKey(roadName);
  if (named && region.scoreByKey.has(named)) return region.scoreByKey.get(named)!;
  return null;
}

export interface RegionCoverage {
  regionId: string | null;
  regionLabel: string | null;
  preferM: number; // meters on score-3 roads
  likeM: number; // meters on score-2 roads
  avoidM: number; // meters on score-0 roads
  preferNames: string[];
  avoidNames: string[];
}

const EMPTY_COVERAGE: RegionCoverage = {
  regionId: null,
  regionLabel: null,
  preferM: 0,
  likeM: 0,
  avoidM: 0,
  preferNames: [],
  avoidNames: [],
};

/** Sum how far a route travels on indexed roads, bucketed by score, within the
 *  one region (if any) the route passes through. */
export function regionRoadCoverage(
  steps: Array<{ roadName?: string; distance?: number }> | undefined,
  routeCoords: LngLat[] | undefined,
): RegionCoverage {
  const region = regionForCoords(routeCoords);
  if (!region || !steps || steps.length === 0) return EMPTY_COVERAGE;
  const out: RegionCoverage = {
    regionId: region.id,
    regionLabel: region.label,
    preferM: 0,
    likeM: 0,
    avoidM: 0,
    preferNames: [],
    avoidNames: [],
  };
  for (const st of steps) {
    const score = regionScoreForRoad(region, st.roadName);
    if (score == null) continue;
    const m = st.distance ?? 0;
    if (score === 3) {
      out.preferM += m;
      if (st.roadName && !out.preferNames.includes(st.roadName)) out.preferNames.push(st.roadName);
    } else if (score === 2) {
      out.likeM += m;
      if (st.roadName && !out.preferNames.includes(st.roadName)) out.preferNames.push(st.roadName);
    } else if (score === 0) {
      out.avoidM += m;
      if (st.roadName && !out.avoidNames.includes(st.roadName)) out.avoidNames.push(st.roadName);
    }
  }
  return out;
}

/** Pleasant-score adjustment from region coverage. Identical math to the Hudson
 *  lever: reward the 3s/2s, penalize the 0s, bounded so it can't fully dominate. */
export function regionRoadScoreAdjustment(cov: RegionCoverage): number {
  const mi = (m: number) => m / 1609.344;
  const boost = Math.min(36, mi(cov.preferM) * 9 + mi(cov.likeM) * 4.5);
  const penalty = Math.min(40, mi(cov.avoidM) * 10);
  return boost - penalty;
}
